import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Input,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AppModal } from '../../shared/components/AppModal';
import { IconAlertCircle } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import { CurrencySelect } from '../../shared/components/CurrencySelect';
import { useTenant } from '../tenant/useTenant';
import { useItemTypes, useUoms } from '../lookups/useLookups';
import { usePermissions } from '../auth/usePermissions';
import { useWarehouses } from '../warehouses/useWarehouses';
import { ItemConversionRows } from './ItemConversionRows';
import { ItemDocuments } from './ItemDocuments';
import { ItemPhotos } from './ItemPhotos';
import { ItemUomTable } from './ItemUomTable';
import { ItemPriceTiers } from '../pricing/ItemPriceTiers';
import { getItemProperties } from './api';
import {
  ITEM_DESCRIPTION_MAX,
  ITEM_NUMBER_MAX,
  createItem,
  listItemWarehouses,
  updateItem,
  type ItemDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** null/undefined = create; an item = edit. */
  item?: ItemDto | null;
  onSaved: () => void;
}

interface FormState {
  itemNumber: string;
  description: string;
  primaryUomId: string;
  itemTypeId: string | null;
  isSerialized: boolean;
  isLotControlled: boolean;
  tracksExpiration: boolean;
  defaultShelfLifeDays: number | '';
  standardCost: number | '';
  currencyCode: string;
  isActive: boolean;
  conversionUomId: string | null;
  conversionFactor: number | '';
  conversionUom2Id: string | null;
  conversionFactor2: number | '';
  /** Warehouses that stock the item — the row that makes it visible to planning and ABC. */
  warehouseIds: string[];
  /** The seven legacy fields, added by tenant script 054. See api.ts for what each one is. */
  upc: string;
  model: string;
  salesDescription: string;
  description2: string;
  description3: string;
  unitPrice: number | '';
  requiresInspection: boolean;
}

const EMPTY: FormState = {
  itemNumber: '',
  description: '',
  primaryUomId: '',
  itemTypeId: null,
  isSerialized: false,
  isLotControlled: false,
  tracksExpiration: false,
  defaultShelfLifeDays: '',
  standardCost: '',
  currencyCode: 'USD',
  isActive: true,
  conversionUomId: null,
  conversionFactor: '',
  conversionUom2Id: null,
  conversionFactor2: '',
  warehouseIds: [],
  upc: '',
  model: '',
  salesDescription: '',
  description2: '',
  description3: '',
  unitPrice: '',
  requiresInspection: false,
};

export function ItemFormModal({ opened, onClose, item, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = Boolean(item);

  const tenant = useTenant();
  const uoms = useUoms();
  const itemTypes = useItemTypes();
  const warehouses = useWarehouses();
  // The exclusion toggles write immediately rather than on Save, so they need their own gate —
  // the form's own submit button is already governed by the route that opened this modal.
  const canWrite = usePermissions().has('items:write');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const activeWarehouses = useMemo(
    () => (warehouses.data ?? []).filter((w) => w.isActive),
    [warehouses.data],
  );

  /**
   * Does this item hold stock, and therefore have settings the database will refuse to change?
   *
   * Taken from the item being edited rather than fetched: usp_Item_GetById and usp_Item_List both
   * carry it, so the list this dialog was opened from already knew. A new item holds nothing.
   *
   * The switches are disabled from this rather than the save being allowed to fail — but the save
   * still handles the refusal, because stock can arrive between opening the form and pressing save.
   */
  const stockedWarehouses = item?.stockedWarehouses ?? 0;
  const stockedQty = item?.stockedQty ?? 0;
  const stocked = stockedWarehouses > 0;

  // On order blocks deactivation but NOT the tracking switches, so it is a separate flag rather
  // than folded into `stocked`. Nothing has been received yet, so no balance would be
  // reinterpreted.
  const onOrder = (item?.onOrderWarehouses ?? 0) > 0;
  const onOrderQty = item?.onOrderQty ?? 0;
  const cannotDeactivate = stocked || onOrder;

  // Which warehouses currently stock this item. Only fetched while editing — a new item has
  // none yet, and its default comes from the active list instead.
  const assigned = useQuery({
    queryKey: ['item-warehouses', item?.itemId ?? 'new'] as const,
    queryFn: ({ signal }) => listItemWarehouses(item!.itemId, signal),
    enabled: opened && Boolean(item?.itemId),
  });

  // Codes for the equation labels, so the chaining reads off the screen.
  const uomList = useMemo(() => uoms.data ?? [], [uoms.data]);
  // Seeded by the tenant reference data. A serialized item stocks in this and nothing else.
  const eachUomId = useMemo(() => uomList.find((u) => u.uomCode === 'EA')?.uomId ?? '', [uomList]);
  const codeOf = (id: string | null) => uomList.find((u) => u.uomId === id)?.uomCode ?? '';
  const stockingCode = codeOf(form.primaryUomId || null);

  // Tenant script 077. What the item's history permits. Only on an existing item: a new one has
  // no history by definition, and asking would be a request that can only answer 404.
  const properties = useQuery({
    queryKey: ['item-properties', item?.itemId] as const,
    queryFn: ({ signal }) => getItemProperties(item!.itemId, signal),
    enabled: opened && Boolean(item?.itemId),
  });

  // FAILS OPEN, like usePermissions and useFeatures: "we could not find out" is not "it is
  // locked". The server refuses the change anyway, so the worst case is a picker that turns out
  // to answer 409 rather than a form somebody cannot use.
  const uomLocked = properties.data ? !properties.data.canChangeStockingUom : false;
  const conversionCode = codeOf(form.conversionUomId);

  // A chain must not revisit a unit — the generated factor would be ambiguous. Excluding
  // the taken units from each list prevents the mistake instead of reporting it.
  const conversionUomOptions = useMemo(
    () =>
      uomList
        .filter((u) => u.uomId !== form.primaryUomId && u.uomId !== form.conversionUom2Id)
        .map((u) => ({ value: u.uomId, label: `${u.uomCode} — ${u.displayName}` })),
    [uomList, form.primaryUomId, form.conversionUom2Id],
  );
  const conversionUom2Options = useMemo(
    () =>
      uomList
        .filter((u) => u.uomId !== form.primaryUomId && u.uomId !== form.conversionUomId)
        .map((u) => ({ value: u.uomId, label: `${u.uomCode} — ${u.displayName}` })),
    [uomList, form.primaryUomId, form.conversionUomId],
  );

  const uomOptions = useMemo(
    () =>
      (uoms.data ?? []).map((u) => ({
        value: u.uomId,
        label: `${u.uomCode} — ${u.displayName}`,
      })),
    [uoms.data],
  );
  const itemTypeOptions = useMemo(
    () =>
      (itemTypes.data ?? []).map((it) => ({
        value: it.itemTypeId,
        label: `${it.itemTypeCode} — ${it.displayName}`,
      })),
    [itemTypes.data],
  );

  // Editing waits for the saved assignments too, or the grid would render every box unticked for
  // a moment and invite somebody to "fix" it.
  const warehousesBusy = warehouses.isLoading || (isEdit && assigned.isLoading);

  // Active warehouses, plus any inactive one the item is ALREADY assigned to. An inactive
  // warehouse cannot be newly chosen, but it has to stay on the grid and stay ticked: drop the
  // row and saving would silently detach the item from it.
  const warehouseRows = useMemo(() => {
    const all = warehouses.data ?? [];
    return all
      .filter((w) => w.isActive || form.warehouseIds.includes(w.warehouseId))
      .map((w) => ({
        id: w.warehouseId,
        code: w.warehouseCode,
        name: w.displayName,
        isActive: w.isActive,
      }));
  }, [warehouses.data, form.warehouseIds]);

  function toggleWarehouse(warehouseId: string, assigned: boolean) {
    // Marks the selection as deliberate, so a default arriving late cannot overwrite it —
    // including an intentional "none".
    touchedWarehouses.current = true;
    setForm((f) => ({
      ...f,
      warehouseIds: assigned
        ? [...f.warehouseIds, warehouseId]
        : f.warehouseIds.filter((id) => id !== warehouseId),
    }));
  }

  // A new item inherits the tenant's default currency, same as warehouses.
  const defaultCurrency = tenant.data?.defaultCurrencyCode ?? EMPTY.currencyCode;

  // A new item defaults to every active warehouse. The asymmetry justifies it: over-assigning
  // puts an item in a planning grid nobody plans it in, which is harmless and one click to
  // undo, whereas under-assigning makes it invisible to planning and skipped by ABC — and
  // posting inventory does not require the assignment, so nothing later would surface the
  // omission. The field is right here on the form, so it is never a silent decision.
  //
  // Legacy defaulted to the user's OWN warehouses (sp_WebItemMaster_Save reads the employee's
  // domain membership). tbl_UserWarehouseAccess exists here for exactly that, but nothing
  // populates it yet, so keying the default off it would assign every new item to nothing.
  const defaultWarehouseIds = useMemo(
    () => activeWarehouses.map((w) => w.warehouseId),
    [activeWarehouses],
  );

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm(
      item
        ? {
            itemNumber: item.itemNumber,
            description: item.description,
            primaryUomId: item.primaryUomId,
            itemTypeId: item.itemTypeId,
            isSerialized: item.isSerialized,
            isLotControlled: item.isLotControlled,
            tracksExpiration: item.tracksExpiration,
            defaultShelfLifeDays: item.defaultShelfLifeDays ?? '',
            standardCost: item.standardCost ?? '',
            currencyCode: item.currencyCode,
            isActive: item.isActive,
            conversionUomId: item.conversionUomId,
            conversionFactor: item.conversionFactor ?? '',
            conversionUom2Id: item.conversionUom2Id,
            conversionFactor2: item.conversionFactor2 ?? '',
            // Filled in by the effect below once the assignments arrive; starting from [] here
            // and overwriting would briefly render the item as stocked nowhere.
            warehouseIds: [],
            // Null on the wire means "not filled in"; the inputs want '' — see Trimmed() on the
            // API side, which folds it back the other way on save.
            upc: item.upc ?? '',
            model: item.model ?? '',
            salesDescription: item.salesDescription ?? '',
            description2: item.description2 ?? '',
            description3: item.description3 ?? '',
            unitPrice: item.unitPrice ?? '',
            requiresInspection: item.requiresInspection ?? false,
          }
        : { ...EMPTY, currencyCode: defaultCurrency, warehouseIds: defaultWarehouseIds },
    );
    touchedWarehouses.current = false;
    // Deps are ONLY what identifies the record being edited.
    //
    // defaultCurrency and defaultWarehouseIds used to be here too, and that was a real bug: both
    // derive from queries that resolve after the modal opens, so their arrival re-ran this effect
    // and reset the entire form — silently discarding anything already typed. Reachable by anyone
    // who starts typing before the reference data lands, and it is what made the warehouse-default
    // test flaky under load. The two seed effects below apply those defaults without touching the
    // rest of the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, item]);

  /**
   * Tracks whether the operator has edited the warehouse selection, so a late-arriving default
   * cannot overwrite a deliberate choice — including a deliberate "none".
   */
  const touchedWarehouses = useRef(false);

  // Seed the default warehouses when they arrive, for a NEW item only, and only while the
  // selection is still untouched.
  useEffect(() => {
    if (!opened || item || touchedWarehouses.current) return;
    setForm((f) => ({ ...f, warehouseIds: defaultWarehouseIds }));
  }, [opened, item, defaultWarehouseIds]);

  // Same for the tenant's default currency. Only overwrites the platform fallback, so a currency
  // the operator picked in the meantime survives.
  useEffect(() => {
    if (!opened || item) return;
    setForm((f) => (f.currencyCode === EMPTY.currencyCode ? { ...f, currencyCode: defaultCurrency } : f));
  }, [opened, item, defaultCurrency]);

  // Editing: adopt the saved assignments as soon as they load. Kept separate from the reset
  // above because that runs before the fetch resolves.
  useEffect(() => {
    if (!opened || !item || !assigned.data) return;
    setForm((f) => ({ ...f, warehouseIds: assigned.data.map((w) => w.warehouseId) }));
  }, [opened, item, assigned.data]);

  const mutation = useMutation({
    mutationFn: () => {
      // Shelf life only means anything for an item that tracks expiration, so
      // don't persist a stale number left behind by toggling the switch off.
      const shelfLife = form.tracksExpiration && form.defaultShelfLifeDays !== ''
        ? Number(form.defaultShelfLifeDays)
        : null;
      const cost = form.standardCost === '' ? null : Number(form.standardCost);

      // The seven from 054, identical on both paths. Text goes over as typed: the API trims it
      // and folds blank to null, so exactly one place decides what "not filled in" means.
      const legacyFields = {
        upc: form.upc,
        model: form.model,
        salesDescription: form.salesDescription,
        description2: form.description2,
        description3: form.description3,
        unitPrice: form.unitPrice === '' ? null : Number(form.unitPrice),
        requiresInspection: form.requiresInspection,
      };

      return isEdit && item
        ? updateItem(item.itemId, {
            description: form.description.trim(),
            primaryUomId: form.primaryUomId,
            itemTypeId: form.itemTypeId,
            isSerialized: form.isSerialized,
            isLotControlled: form.isLotControlled,
            tracksExpiration: form.tracksExpiration,
            defaultShelfLifeDays: shelfLife,
            standardCost: cost,
            currencyCode: form.currencyCode.trim().toUpperCase(),
            isActive: form.isActive,
            conversionUomId: form.conversionUomId,
            conversionFactor: form.conversionUomId && form.conversionFactor !== '' ? Number(form.conversionFactor) : null,
            conversionUom2Id: form.conversionUom2Id,
            conversionFactor2: form.conversionUom2Id && form.conversionFactor2 !== '' ? Number(form.conversionFactor2) : null,
            // Always sent, including when empty: the form owns this field, so omitting it would
            // mean "leave alone" on update and "assign everything" on create — neither of which
            // is what the operator sees on screen.
            warehouseIds: form.warehouseIds,
            ...legacyFields,
          })
        : createItem({
            itemNumber: form.itemNumber.trim(),
            description: form.description.trim(),
            primaryUomId: form.primaryUomId,
            itemTypeId: form.itemTypeId,
            isSerialized: form.isSerialized,
            isLotControlled: form.isLotControlled,
            tracksExpiration: form.tracksExpiration,
            defaultShelfLifeDays: shelfLife,
            standardCost: cost,
            currencyCode: form.currencyCode.trim().toUpperCase() || null,
            isActive: form.isActive,
            conversionUomId: form.conversionUomId,
            conversionFactor: form.conversionUomId && form.conversionFactor !== '' ? Number(form.conversionFactor) : null,
            conversionUom2Id: form.conversionUom2Id,
            conversionFactor2: form.conversionUom2Id && form.conversionFactor2 !== '' ? Number(form.conversionFactor2) : null,
            // Always sent, including when empty: the form owns this field, so omitting it would
            // mean "leave alone" on update and "assign everything" on create — neither of which
            // is what the operator sees on screen.
            warehouseIds: form.warehouseIds,
            ...legacyFields,
          });
    },
    onSuccess: () => {
      notifications.show({
        message: isEdit ? t('items.updated') : t('items.created'),
        color: 'green',
      });
      onSaved();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        // Two different 409s now. The stock conflict carries the warehouse codes that blocked
        // the change, and that message is the only thing telling the operator which warehouse
        // to empty — so it is shown verbatim rather than replaced with a generic string.
        if (err.problem.type?.endsWith('item_warehouse_has_stock')) {
          setSubmitError(err.problem.title ?? t('items.errors.warehouseHasStock'));
          return;
        }
        setErrors((e) => ({ ...e, itemNumber: t('items.errors.numberExists') }));
        return;
      }
      setSubmitError(t('items.errors.saveFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: typeof errors = {};
    if (!isEdit && !form.itemNumber.trim()) next.itemNumber = t('items.errors.numberRequired');
    if (!form.description.trim()) next.description = t('items.errors.descriptionRequired');
    if (!form.primaryUomId) next.primaryUomId = t('items.errors.uomRequired');

    // Mirrors ValidateUomChain in ItemEndpoints and the CK_tbl_ItemMaster_Conversion*
    // constraints. Caught here so the operator sees the field at fault rather than a
    // whole-form error from the API.
    if (form.conversionUomId && form.conversionFactor === '') {
      next.conversionFactor = t('items.errors.conversionFactorRequired');
    }
    if (form.conversionUomId && Number(form.conversionFactor) <= 0) {
      next.conversionFactor = t('items.errors.conversionFactorPositive');
    }
    if (form.conversionUomId && form.conversionUomId === form.primaryUomId) {
      next.conversionUomId = t('items.errors.conversionUomDistinct');
    }
    // The second conversion is defined relative to the first, so it cannot stand alone.
    if (form.conversionUom2Id && !form.conversionUomId) {
      next.conversionUom2Id = t('items.errors.conversionSecondNeedsFirst');
    }
    if (form.conversionUom2Id && form.conversionFactor2 === '') {
      next.conversionFactor2 = t('items.errors.conversionFactorRequired');
    }
    if (form.conversionUom2Id && Number(form.conversionFactor2) <= 0) {
      next.conversionFactor2 = t('items.errors.conversionFactorPositive');
    }
    if (
      form.conversionUom2Id &&
      (form.conversionUom2Id === form.primaryUomId || form.conversionUom2Id === form.conversionUomId)
    ) {
      next.conversionUom2Id = t('items.errors.conversionUom2Distinct');
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('items.editTitle') : t('items.createTitle')}
      width="xl"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          {/* Two columns at md and above, one below. Eleven sections stacked in a single column
              ran nearly the full height of a 1440p monitor, which put the save buttons a scroll
              away and made the form hard to scan. Identity and costing on the left; everything
              DERIVED — warehouses, conversions, the generated units — on the right, so the two
              halves read as cause and effect. */}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="md">
            <Stack gap="md">
              <Divider label={t('items.detailsSection')} labelPosition="left" />

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput
                  label={t('items.number')}
                  value={form.itemNumber}
                  onChange={(e) => setForm({ ...form, itemNumber: e.currentTarget.value })}
                  error={errors.itemNumber}
                  disabled={isEdit}
                  description={isEdit ? t('items.numberImmutable') : undefined}
                  maxLength={ITEM_NUMBER_MAX}
                  required={!isEdit}
                  autoFocus={!isEdit}
                />
                <Select
                  label={t('items.itemType')}
                  placeholder={t('items.itemTypeNone')}
                  data={itemTypeOptions}
                  value={form.itemTypeId}
                  onChange={(v) => setForm({ ...form, itemTypeId: v })}
                  searchable
                  clearable
                  nothingFoundMessage={t('shell.noMatch')}
                  disabled={itemTypes.isLoading}
                />
              </SimpleGrid>

              {/* Both sit on legacy's General Information row. maxLength matches the column so a
                  value cannot be typed that the save would silently truncate. */}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput
                  label={t('items.upc')}
                  value={form.upc}
                  onChange={(e) => setForm({ ...form, upc: e.currentTarget.value })}
                  maxLength={50}
                />
                <TextInput
                  label={t('items.model')}
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.currentTarget.value })}
                  maxLength={20}
                />
              </SimpleGrid>

              <Textarea
                label={t('items.description')}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
                error={errors.description}
                maxLength={ITEM_DESCRIPTION_MAX}
                autosize
                minRows={2}
                maxRows={4}
                required
                autoFocus={isEdit}
              />

              {/* What the CUSTOMER sees, as against the description above, which is what the
                  warehouse sees. Legacy labels it "Description Sales"; the column is
                  SalesDescription and this label reads as English. */}
              <Textarea
                label={t('items.salesDescription')}
                description={t('items.salesDescriptionHint')}
                value={form.salesDescription}
                onChange={(e) => setForm({ ...form, salesDescription: e.currentTarget.value })}
                maxLength={ITEM_DESCRIPTION_MAX}
                autosize
                minRows={1}
                maxRows={3}
              />

              {/* Single-line, where legacy uses textareas. They are 255 characters of free text
                  with no defined meaning, and two more multi-line boxes would spend a third of the
                  form's height on fields most tenants leave empty. */}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput
                  label={t('items.description2')}
                  value={form.description2}
                  onChange={(e) => setForm({ ...form, description2: e.currentTarget.value })}
                  maxLength={255}
                />
                <TextInput
                  label={t('items.description3')}
                  value={form.description3}
                  onChange={(e) => setForm({ ...form, description3: e.currentTarget.value })}
                  maxLength={255}
                />
              </SimpleGrid>

              {/* Full column width now. It used to sit alone inside a two-column grid, leaving
                  half a row of dead space on every render. */}
              {/* Pinned to Each and locked once the item is serialized. A serial number
                  identifies one physical thing, so a serialized item is 1:1 by definition —
                  there is no such thing as a case of a serial number. The server refuses
                  anything else (51910). */}
              <Select
                label={t('items.primaryUom')}
                description={
                  uomLocked
                    ? t('items.stockingUomLocked')
                    : form.isSerialized
                      ? t('items.serializedStocksInEach')
                      : undefined
                }
                placeholder={t('items.primaryUomPlaceholder')}
                data={uomOptions}
                value={form.primaryUomId || null}
                onChange={(v) => setForm({ ...form, primaryUomId: v ?? '' })}
                error={errors.primaryUomId}
                searchable
                allowDeselect={false}
                nothingFoundMessage={t('shell.noMatch')}
                disabled={uoms.isLoading || form.isSerialized || uomLocked}
                required
              />

              {/* Three across now. Price sits beside cost because they share the currency to
                  their right, and because legacy puts them on one row. Nothing consumes the price
                  yet: volume pricing is the discount taken off it, and that is not built. */}
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <NumberInput
                  label={t('items.standardCost')}
                  value={form.standardCost}
                  onChange={(v) => setForm({ ...form, standardCost: v === '' ? '' : Number(v) })}
                  min={0}
                  decimalScale={4}
                  step={0.01}
                  thousandSeparator=","
                />
                <NumberInput
                  label={t('items.unitPrice')}
                  value={form.unitPrice}
                  onChange={(v) => setForm({ ...form, unitPrice: v === '' ? '' : Number(v) })}
                  min={0}
                  decimalScale={4}
                  step={0.01}
                  thousandSeparator=","
                />
                <CurrencySelect
                  label={t('items.currency')}
                  value={form.currencyCode}
                  onChange={(v) => setForm({ ...form, currencyCode: v })}
                />
              </SimpleGrid>

            </Stack>

            <Stack gap="md">
              <Divider label={t('items.warehousesSection')} labelPosition="left" />

              {/*
                The assignment this control writes is what makes the item visible to Inventory
                Planning and eligible for ABC classification and cycle counting. Posting inventory
                does NOT require it, so an item left unassigned can be transacted for months while
                never being counted — which is why this sits on the item form rather than being
                reachable only from the planning screen.
              */}
              {/*
                A GRID rather than a multi-select, matching legacy's Domains panel, because the
                question an operator actually asks is "which warehouses is this item NOT in?" — and
                a combobox can only answer the positive. Every warehouse gets a row whether it is
                ticked or not, so the negative is readable at a glance instead of being inferred
                from an absence inside a dropdown. It matters most for the 3PL tenant, where each
                warehouse is a different end client and putting an item in the wrong one is a
                confidentiality problem rather than a tidiness one.

                labelElement="div" because the label heads a table, not a single control — a bare
                <label> pointing at nothing is worse for a screen reader than no label at all.
              */}
              <Input.Wrapper
                label={t('items.warehouses')}
                description={t('items.warehousesHint')}
                labelElement="div"
              >
                <Card withBorder padding={0} mt={6}>
                  {/* Capped and scrollable: a 3PL can carry a dozen warehouses, and this sits
                      inside a modal that already scrolls on a phone. */}
                  <Table.ScrollContainer minWidth={260} mah={240}>
                    <Table striped highlightOnHover stickyHeader>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th w={110} ta="center">
                            {t('items.warehouseGrid.assigned')}
                          </Table.Th>
                          <Table.Th>{t('items.warehouseGrid.warehouse')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {warehouseRows.map((w) => (
                          <Table.Tr key={w.id} opacity={w.isActive ? 1 : 0.55}>
                            <Table.Td ta="center">
                              <Checkbox
                                checked={form.warehouseIds.includes(w.id)}
                                onChange={(e) => toggleWarehouse(w.id, e.currentTarget.checked)}
                                // Its own name: the text is in the next cell, so without this a
                                // screen reader reads a column of unlabelled checkboxes.
                                aria-label={`${w.code} — ${w.name}`}
                                disabled={warehousesBusy}
                              />
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs" wrap="nowrap">
                                <Text size="sm" fw={600}>
                                  {w.code}
                                </Text>
                                {!w.isActive && (
                                  <Badge size="xs" variant="light" color="gray">
                                    {t('items.warehouseInactive')}
                                  </Badge>
                                )}
                              </Group>
                              <Text size="xs" c="dimmed">
                                {w.name}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        ))}

                        {warehousesBusy && warehouseRows.length === 0 && (
                          <Table.Tr>
                            <Table.Td colSpan={2}>
                              <Group justify="center" py="sm">
                                <Loader size="xs" />
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        )}

                        {!warehousesBusy && warehouseRows.length === 0 && (
                          <Table.Tr>
                            <Table.Td colSpan={2}>
                              <Text size="sm" c="dimmed" ta="center" py="sm">
                                {t('items.warehousesNoneExist')}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Card>
              </Input.Wrapper>

              {/* Not an error — an item deliberately stocked nowhere is legitimate. But it is a
                  consequence worth stating, because nothing downstream would ever mention it. */}
              {form.warehouseIds.length === 0 && (
                <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                  {t('items.warehousesEmptyWarning')}
                </Alert>
              )}

              {warehouses.isSuccess && activeWarehouses.length === 0 && (
                <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
                  {t('items.warehousesNoneExist')}
                </Alert>
              )}

              <Divider label={t('items.trackingSection')} labelPosition="left" />

              {/* Said once, above the switches it explains, rather than three identical tooltips on
                  three disabled controls. */}
              {stocked && (
                <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                  {t('items.lockedByStock', {
                    qty: stockedQty,
                    count: stockedWarehouses,
                  })}
                </Alert>
              )}

              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                {/* Turning this on makes the item 1:1: it stocks in Each and loses any
                    conversions, because a serial number counts one physical thing. Applied here
                    rather than left for the save to reject, so the form never shows a
                    combination the server will refuse. */}
                <Switch
                  label={t('items.isSerialized')}
                  checked={form.isSerialized}
                  disabled={stocked}
                  onChange={(e) => {
                    const isSerialized = e.currentTarget.checked;
                    if (!isSerialized) {
                      setForm({ ...form, isSerialized });
                      return;
                    }
                    setForm({
                      ...form,
                      isSerialized,
                      primaryUomId: eachUomId || form.primaryUomId,
                      conversionUomId: null,
                      conversionFactor: '',
                      conversionUom2Id: null,
                      conversionFactor2: '',
                    });
                  }}
                />
                <Switch
                  label={t('items.isLotControlled')}
                  checked={form.isLotControlled}
                  disabled={stocked}
                  onChange={(e) => setForm({ ...form, isLotControlled: e.currentTarget.checked })}
                />
                <Switch
                  label={t('items.tracksExpiration')}
                  checked={form.tracksExpiration}
                  disabled={stocked}
                  onChange={(e) => setForm({ ...form, tracksExpiration: e.currentTarget.checked })}
                />
                {/* NOT disabled by stock, unlike the three above. Those decide what an existing
                    balance MEANS, which is why the database freezes them once there is one. This
                    is a note to whoever receives the next delivery and can change at any time.
                    Nothing acts on it yet — legacy does not either. */}
                <Switch
                  label={t('items.requiresInspection')}
                  checked={form.requiresInspection}
                  onChange={(e) => setForm({ ...form, requiresInspection: e.currentTarget.checked })}
                />
              </SimpleGrid>

              {/* Only meaningful when the item tracks expiration. */}
              {form.tracksExpiration && (
                <NumberInput
                  label={t('items.shelfLifeDays')}
                  description={t('items.shelfLifeHint')}
                  value={form.defaultShelfLifeDays}
                  onChange={(v) =>
                    setForm({ ...form, defaultShelfLifeDays: v === '' ? '' : Number(v) })
                  }
                  min={0}
                  allowDecimal={false}
                  w={{ base: '100%', sm: 240 }}
                />
              )}

              {/* Only DEACTIVATING is refused, so the toggle stays usable for an item that is
                  already inactive — otherwise a stocked inactive item could never be turned back
                  on, which is the opposite of what the rule is for. */}
              <Switch
                label={t('items.active')}
                checked={form.isActive}
                disabled={cannotDeactivate && form.isActive}
                description={
                  cannotDeactivate && form.isActive
                    ? stocked
                      ? t('items.cannotDeactivateStocked')
                      : t('items.cannotDeactivateOnOrder', { qty: onOrderQty })
                    : undefined
                }
                onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
              />
            </Stack>
          </SimpleGrid>

          {/* Full width, and laid out like the current product's U/M Conversion tab: the entry rows
              on the left, the generated result on the right. Stacking them inside a column made
              that column far taller than the other and put the answer below the question. */}
          {/* Editing only. A photo needs an item to belong to, and on a new item there is not
              one yet — offering the control would mean holding files in memory and posting them
              after the save, with a half-saved state if that second step failed. */}
          {isEdit && item && (
            <>
              <Divider label={t('items.photos.section')} labelPosition="left" />
              <ItemPhotos itemId={item.itemId} canWrite={canWrite} />

              <Divider label={t('items.documents.section')} labelPosition="left" />
              <ItemDocuments itemId={item.itemId} canWrite={canWrite} />
            </>
          )}

          <Divider label={t('items.conversionSection')} labelPosition="left" />

          {/* A serialized item has nothing to convert, so the rows are replaced by the reason
              rather than shown disabled — a greyed-out form invites people to look for the way to
              enable it. */}
          {form.isSerialized ? (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('items.conversionSerialized')}
            </Alert>
          ) : (
            <>
          <Text size="sm" c="dimmed">
            {t('items.conversionHint')}
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="md">
            <ItemConversionRows
              stockingCode={stockingCode}
              conversionCode={conversionCode}
              conversionUomId={form.conversionUomId}
              conversionFactor={form.conversionFactor}
              conversionUom2Id={form.conversionUom2Id}
              conversionFactor2={form.conversionFactor2}
              conversionUomOptions={conversionUomOptions}
              conversionUom2Options={conversionUom2Options}
              errors={{
                conversionUomId: errors.conversionUomId,
                conversionFactor: errors.conversionFactor,
                conversionUom2Id: errors.conversionUom2Id,
                conversionFactor2: errors.conversionFactor2,
              }}
              disabled={uoms.isLoading}
              noStockingUom={!form.primaryUomId}
              onChange={(next) => setForm({ ...form, ...next })}
            />

            <Stack gap={4}>
              {/* The generated RESULT of the rows beside it, not more fields: the server chains
                  them and fans them through the tenant-wide physical conversions, so the list is
                  normally longer than what was typed. */}
              <Text size="sm" fw={500}>
                {t('items.uomTable.title')}
              </Text>
              <Text size="xs" c="dimmed" mb={4}>
                {t('items.uomTable.subtitle')}
              </Text>
              <ItemUomTable itemId={item?.itemId ?? null} canWrite={canWrite} />
            </Stack>

            {/* Behind pricing:read, so it renders nothing at all for a picker. The stocking code
                goes with it because that is the unit every tier boundary is measured in. */}
            <ItemPriceTiers itemId={item?.itemId ?? null} stockingUomCode={stockingCode} />
          </SimpleGrid>
            </>
          )}

          {submitError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {isEdit ? t('shell.saveChanges') : t('items.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
